import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';

export interface Zone {
  id: string;
  nom: string;
  code: string;
  theme?: string;
  createdAt: string;
  updatedAt: string;
  objets?: Array<{
    id: string;
    nom: string;
    quantite: number;
    etat: string;
  }>;
  boxes?: Array<{
    id: string;
    nom: string;
    code: string;
  }>;
}

export const useZones = () => {
  return useQuery({
    queryKey: ['zones'],
    queryFn: async (): Promise<Zone[]> => {
      const response = await apiClient.get('/zones');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useZone = (id: string) => {
  return useQuery({
    queryKey: ['zone', id],
    queryFn: async (): Promise<Zone> => {
      const response = await apiClient.get(`/zones/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCreateZone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Zone>) => {
      const response = await apiClient.post('/zones', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones'] });
    },
  });
};

export const useUpdateZone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Zone> }) => {
      const response = await apiClient.patch(`/zones/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['zones'] });
      queryClient.invalidateQueries({ queryKey: ['zone', id] });
    },
  });
};

export const useDeleteZone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/zones/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones'] });
    },
  });
};

